import { IconButton, Label, Link, MessageBar, MessageBarType, Separator, Stack, Text } from "@fluentui/react";
import { RootState } from "app/store";
import EntityLink from "components/controls/EntityLink";
import LookupControlDialog from "components/controls/LookupControlDialog";
import { fadeIn } from "global/Application";
import {
    IEntityReferenceValue as FAM_IEntityReferenceValue
} from "model/FlexibleAttributeModel";
import { IEntityReferenceValue } from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { IGroupConfigurationDataProperties } from "../IGroupConfigurationDataProperties";


interface IFilterCondition {
    context: 'condition';
    attribute: string;
    operator: 'eq' | 'gt';
    value: string | number
}

interface ILinkEntity {
    context: 'link-entity';
    name: string;
    to: string;
    from: string;
    alias: string;
    linkType: 'inner' | '?';
    children?: IFilterGroup[];
}

interface IFilterGroup {
    context: 'group';
    type: 'and' | 'or';
    // TODO remove prepend, parent should call for addition of sibling
    prepend?: ILinkEntity[];
    conditions?: IFilterCondition[];
}


interface IRelatedTaskProps {
    entity: IEntityReferenceValue;
    onDelete: (entity: IEntityReferenceValue) => void;
    disabled: boolean;
}

const RelatedTask: React.FunctionComponent<IRelatedTaskProps> = (props) => {
    return <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: '5px' }}>
        <Stack.Item>
            <EntityLink entity={props.entity} />
        </Stack.Item>
        <Stack.Item grow={1} >
            <Separator />
        </Stack.Item>
        {!props.disabled &&
            <Stack.Item>
                {/* <Link >(Remove)</Link> */}
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => props.onDelete?.(props.entity)}
                />
            </Stack.Item>
        }
    </Stack>
}

interface IProps extends TComponentConnectedProps {
    value: IEntityReferenceValue[];
    onDelete: (entity: IEntityReferenceValue) => void;
    onAddOrUpdate: (entity: IEntityReferenceValue) => void;
    disabled: boolean;
    creatingFrom: boolean;
    properties: IGroupConfigurationDataProperties;
}

const Revision: React.FunctionComponent<IProps> = (props) => {

    const [searching, setSearching] = useState(false);
    const [query, setQuery] = useState<string | null>(null);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);


    useEffect(() => {


        function parseExpressionTree(expressionTree: IFilterGroup | IFilterCondition | ILinkEntity) {
            let scope = '';

            if (expressionTree.context === "link-entity") {
                scope += `<link-entity name="${expressionTree.name}" to="${expressionTree.to}" from="${expressionTree.from}" alias="${expressionTree.alias}" link-type="${expressionTree.linkType}">`;
                expressionTree.children?.forEach(e => {
                    scope += parseExpressionTree(e);
                });
                scope += '</link-entity>';
            } else if (expressionTree.context === "condition") {
                scope += `<condition attribute="${expressionTree.attribute}" operator="${expressionTree.operator}" value="${expressionTree.value}" />`;
            } else if (expressionTree.context === 'group') {

                if (expressionTree.prepend && expressionTree.prepend.length > 0)
                    expressionTree.prepend.forEach(ePrep => {
                        scope += parseExpressionTree(ePrep);
                    });

                scope += `<filter type="${expressionTree.type}">`;
                expressionTree.conditions?.forEach(e => {
                    scope += parseExpressionTree(e);
                });
                scope += '</filter>';
            }

            return scope;
        }

        const sequenceNumberCondition: IFilterCondition = {
            context: 'condition',
            attribute: 'bcbs_sequencenumber',
            operator: 'eq',
            value: '02.07',
        };


        const orExpression: IFilterGroup = {
            context: 'group',
            type: 'or',
            conditions: [],
        };

        // if we are creating, current snapshot becomes the originating snapshot
        if (props.creatingFrom) {

            const ifOriginating: IFilterCondition = {
                context: 'condition',
                attribute: 'bcbs_groupconfigurationsnapshotid',
                operator: 'eq',
                value: props.state.data.snapshot.bcbs_groupconfigurationsnapshotid!,
            }

            if (ifOriginating)
                orExpression.conditions!.push(ifOriginating);


        } else {

            const ifOriginating1: IFilterCondition | undefined = props.state.data.snapshot._bcbs_originatingsnapshotid_value
                ? {
                    context: 'condition',
                    attribute: 'bcbs_groupconfigurationsnapshotid',
                    operator: 'eq',
                    value: props.state.data.snapshot._bcbs_originatingsnapshotid_value!,
                }
                : undefined;

            const ifOriginating2: IFilterCondition | undefined = props.state.data.snapshot._bcbs_originatingsnapshot2id_value
                ? {
                    context: 'condition',
                    attribute: 'bcbs_groupconfigurationsnapshotid',
                    operator: 'eq',
                    value: props.state.data.snapshot._bcbs_originatingsnapshot2id_value!,
                }
                : undefined;


            if (ifOriginating1)
                orExpression.conditions!.push(ifOriginating1);

            if (ifOriginating2)
                orExpression.conditions!.push(ifOriginating2);

        }


        if ((orExpression.conditions?.length ?? 0) < 1) {
            setValidationMessage("The originating snapshot was not available.");
            setQuery(null);
            return;
        }


        const groupIdCondition: IFilterCondition = {
            context: 'condition',
            attribute: 'bcbs_groupid',
            operator: 'eq',
            value: props.state.data.snapshot._bcbs_groupid_value!,
        };

        const effectiveDateCondition: IFilterCondition = {
            context: 'condition',
            attribute: 'bcbs_effectivedate',
            operator: 'eq',
            value: props?.properties?.effectiveDate?.value!,
        };

        const linkEntity: ILinkEntity = {
            context: 'link-entity',
            name: 'bcbs_groupconfigurationsnapshot',
            alias: 'bcbs_groupconfigurationsnapshot',
            from: 'bcbs_groupconfigurationsnapshotid',
            to: 'bcbs_groupconfigurationsnapshotid',
            linkType: 'inner',
            children: [
                {
                    context: 'group',
                    type: 'and',
                    conditions: [
                        effectiveDateCondition
                    ]
                }
            ]
        }

        const taskGroups: IFilterGroup = {
            context: 'group',
            type: 'and',
            // children: [
            //     matchSequenceNumber,
            //     orExpression
            // ],
            prepend: [
                linkEntity
            ],
            conditions: [
                sequenceNumberCondition,
                groupIdCondition
            ],
        };

        const fetchXmlFilter = parseExpressionTree(taskGroups);

        console.log("Revision fetchXmlFilter", fetchXmlFilter);

        const viewSearchTask = {
            "viewName": "Test View",
            "entityName": "task",
            "entitySetName": "tasks",
            "fetchXML": `
            <fetch version="1.0" output-format="xml-platform" mapping="logical" distinct="false">
                <entity name="task">
                    <attribute name="subject"/>
                    <attribute name="activityid"/>
                    <attribute name="bcbs_parenttaskid"/>
                    <attribute name="bcbs_estimatedstartdate"/>
                    <attribute name="bcbs_estimatedenddate"/>
                    <attribute name="createdby"/>
                    <order attribute="subject" descending="false"/>
                    ${fetchXmlFilter}
                </entity>
            </fetch>`,
            "layout": {
                "Name": "resultset",
                "Object": 4212,
                "Rows": [
                    {
                        "Name": "result",
                        "Id": "activityid",
                        "Cells": [
                            {
                                "Name": "subject",
                                "Width": 300,
                                "RelatedEntityName": "",
                                "DisableMetaDataBinding": false,
                                "LabelId": "",
                                "IsHidden": false,
                                "DisableSorting": false,
                                "AddedBy": "",
                                "Desc": "",
                                "CellType": "",
                                "ImageProviderWebresource": "",
                                "ImageProviderFunctionName": "",
                                "DisplayName": "Subject",
                                "IsPrimaryId": false,
                                "AttributeType": "String",
                                "IsPrimaryName": true
                            },
                            {
                                "Name": "_createdby_value",
                                "Width": 300,
                                "RelatedEntityName": "",
                                "DisableMetaDataBinding": false,
                                "LabelId": "",
                                "IsHidden": false,
                                "DisableSorting": false,
                                "AddedBy": "",
                                "Desc": "",
                                "CellType": "",
                                "ImageProviderWebresource": "",
                                "ImageProviderFunctionName": "",
                                "DisplayName": "Created By",
                                "IsPrimaryId": false,
                                "AttributeType": "Lookup",
                                "IsPrimaryName": false
                            },
                            {
                                "Name": "bcbs_estimatedenddate",
                                "Width": 100,
                                "RelatedEntityName": "",
                                "DisableMetaDataBinding": false,
                                "LabelId": "",
                                "IsHidden": false,
                                "DisableSorting": false,
                                "AddedBy": "",
                                "Desc": "",
                                "CellType": "",
                                "ImageProviderWebresource": "",
                                "ImageProviderFunctionName": "",
                                "DisplayName": "Estimated End",
                                "IsPrimaryId": false,
                                "AttributeType": "DateTime",
                                "IsPrimaryName": false
                            },
                            {
                                "Name": "bcbs_estimatedstartdate",
                                "Width": 100,
                                "RelatedEntityName": "",
                                "DisableMetaDataBinding": false,
                                "LabelId": "",
                                "IsHidden": false,
                                "DisableSorting": false,
                                "AddedBy": "",
                                "Desc": "",
                                "CellType": "",
                                "ImageProviderWebresource": "",
                                "ImageProviderFunctionName": "",
                                "DisplayName": "Estimated Start",
                                "IsPrimaryId": false,
                                "AttributeType": "DateTime",
                                "IsPrimaryName": false
                            },
                            {
                                "Name": "_bcbs_parenttaskid_value",
                                "Width": 350,
                                "RelatedEntityName": "",
                                "DisableMetaDataBinding": false,
                                "LabelId": "",
                                "IsHidden": false,
                                "DisableSorting": false,
                                "AddedBy": "",
                                "Desc": "",
                                "CellType": "",
                                "ImageProviderWebresource": "",
                                "ImageProviderFunctionName": "",
                                "DisplayName": "Parent Task",
                                "IsPrimaryId": false,
                                "AttributeType": "Lookup",
                                "IsPrimaryName": false
                            }
                        ],
                        "MultiObjectIdField": "",
                        "LayoutStyle": ""
                    }
                ],
                "CustomControlDescriptions": [],
                "Jump": "subject",
                "Select": true,
                "Icon": true,
                "Preview": true,
                "IconRenderer": ""
            }
        }

        let query = JSON.stringify(viewSearchTask);
        setQuery(query);

    }, []);

    const onPick = (entity: FAM_IEntityReferenceValue) => {
        props.onAddOrUpdate(entity);
    }

    const disabledAdd = props.disabled || !query;

    return <>

        <Stack horizontal verticalAlign="center" horizontalAlign="start" tokens={{ childrenGap: '5px' }}>
            <Stack.Item>
                <Label>Revisions</Label>
            </Stack.Item>
            {!disabledAdd &&
                <Stack.Item>
                    <Text variant="small">
                        <Link
                            onClick={(event) => {
                                setSearching(true);
                            }}
                        >
                            (Add)
                        </Link>
                    </Text>
                </Stack.Item>
            }
        </Stack>


        {validationMessage &&
            <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                {validationMessage}
            </MessageBar>
        }

        <Stack>
            {props?.value?.map(e => {
                return <RelatedTask
                    key={e.id}
                    entity={e}
                    onDelete={props.onDelete}
                    disabled={props.disabled}
                />
            })}
        </Stack>

        {/* <LookupControlBase
            placeHolder="add ..."
            onLookup={() => {
                setSearching(true);
            }}
            disabled={props.disabled}
        /> */}

        {query &&
            <LookupControlDialog
                isOpen={!!query && searching}
                label={'Task'}
                variant={'multiple'}
                masterAttribute={{ bcbs_lookupviewdefinition: query }}
                onPick={(picks) => {
                    setSearching(false);

                    if (picks && picks.length > 0) {
                        picks.forEach(eachPick => {
                            onPick(eachPick as FAM_IEntityReferenceValue);
                        });
                    }
                }}
                onCancel={() => {
                    setSearching(false);
                }}
                onDismiss={() => {
                    setSearching(false);
                }}
            />
        }

    </>
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(Revision);

